import { useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import Slider from "react-slick";
import { Video360 } from "./Video360";
import {
  PassengerIcon,
  SuitcaseIcon,
  AromaIcon,
  WifiIcon,
  TvIcon,
  VolumeUpIcon,
  View360Icon,
} from "../../../../components/icons";

import { cn } from "../../../../utils/classnames";
import { createTypographyHelper } from "../../../../utils/typography";
import { BookSection } from "../../../../components/book-section/BookSection";
import LazyImage from "../../../../components/common/LazyImage";
import { FiArrowLeft, FiArrowRight, FiX } from "react-icons/fi";
import { Link } from "react-router-dom";
import { useDisclosure } from "../../../../hooks";
import { MotionLightbox } from "../../../../components/motion";

const WELLCabinGranluxeBanner = "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/public_folder/cms-image/cms-fleet-types/well-cabin-granluxe/banner.avif";
const WELLCabinGranluxeImage = "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/public_folder/cms-image/cms-fleet-types/well-cabin-granluxe/well-cabin-granluxe-exterior.webp";
const slideImage1 = "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/public_folder/cms-image/cms-fleet-types/well-cabin-granluxe/slide-image/01-exterior-1.webp";
const slideImage2 = "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/public_folder/cms-image/cms-fleet-types/well-cabin-granluxe/slide-image/02-exterior-2.webp";
const slideImage3 = "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/public_folder/cms-image/cms-fleet-types/well-cabin-granluxe/slide-image/03-interior-1.webp";
const slideImage4 = "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/public_folder/cms-image/cms-fleet-types/well-cabin-granluxe/slide-image/04-interior-2.webp";
const slideImage5 = "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/public_folder/cms-image/cms-fleet-types/well-cabin-granluxe/slide-image/05-interior-3.webp";
const slideImage6 = "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/public_folder/cms-image/cms-fleet-types/well-cabin-granluxe/slide-image/06-exterior-3.webp";
const PanasonicAutomotiveImageBackgroundWhite = "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/public_folder/cms-image/cms-fleet-types/panasonic-automotive-bg-white.png";
const PanasonicAutomotiveImageBackgroundBlack = "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/public_folder/cms-image/cms-fleet-types/panasonic-automotive-bg-black.png";
const WELLCabinGranluxeSpecificationImage = "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/public_folder/cms-image/cms-fleet-types/well-cabin-granluxe/seat-map.avif";
const LightingImage = "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/public_folder/cms-image/cms-fleet-types/well-cabin-granluxe/feature-aminities/lighting.webp";
const DisplayImage = "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/public_folder/cms-image/cms-fleet-types/well-cabin-granluxe/feature-aminities/display.webp";
const AudioImage = "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/public_folder/cms-image/cms-fleet-types/well-cabin-granluxe/feature-aminities/audio.webp";
const AromaImage = "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/public_folder/cms-image/cms-fleet-types/well-cabin-granluxe/feature-aminities/aroma.webp";


const CabinGalleryArrow = ({ direction, disabled, onClick }) => {
  const Icon = direction === "next" ? FiArrowRight : FiArrowLeft;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={direction === "next" ? "Next image" : "Previous image"}
      className={cn(
        "tw:rounded-full! tw:shrink-0 tw:flex tw:items-center tw:justify-center tw:border tw:transition-colors tw:w-[30px] tw:h-[30px] tw:bg-black",
        disabled
          ? "tw:border-[#D4D4D4] tw:opacity-40 tw:cursor-default"
          : "tw:border-[#282828] tw:hover:bg-[#282828]"
      )}
    >
      <Icon size={16} color="white" />
    </button>
  );
};

// Each amenity icon is drawn with a different amount of empty padding inside its
// own SVG viewBox, so giving them all the same `size` renders them at visibly
// different sizes. These per-icon values make the drawn artwork land at roughly
// the same optical size (~18px) inside the shared box below.
const AMENITY_ICON_SIZE = {
  passenger: 28,
  suitcase: 28,
  tv: 19,
  volumeUp: 35,
  aroma: 25,
  wifi: 19,
};

const AMENITY_ICON_COLOR = "#A7A7A7";

// Fixed-size box so every amenity icon occupies the same space and the labels
// underneath all start at the same height.
const AmenityIcon = ({ children }) => (
  <div className="tw:flex tw:items-center tw:justify-center tw:h-[36px] tw:w-[36px]">
    {children}
  </div>
);

function WELLCabinGranluxe({ tabList }) {
  const { t, i18n } = useTranslation();
  const { push } = useHistory();
  const getTypographyClass = createTypographyHelper(i18n.language);
  const lightboxSliderRef = useRef(null);
  const cabinGalleryRef = useRef(null);
  const pendingGalleryScrollFrame = useRef(null);
  const [canScrollGalleryPrev, setCanScrollGalleryPrev] = useState(false);
  const [canScrollGalleryNext, setCanScrollGalleryNext] = useState(false);
  const [activeCabinImageIndex, setActiveCabinImageIndex] = useState(0);
  const {
    isOpen: isCabinLightboxOpen,
    onOpen: onOpenCabinLightbox,
    onClose: onCloseCabinLightbox,
  } = useDisclosure(false);

  const isJapanese = i18n.language === "jp";
  const cabinGalleryImages = [
    {
      src: slideImage1,
      highSrc: slideImage1,
      alt: "WELL Cabin GranLuxe Exterior 1",
      class: "tw:object-[60%_40%]"
    },
    {
      src: slideImage2,
      highSrc: slideImage2,
      alt: "WELL Cabin GranLuxe Exterior 2",
      class: "tw:object-[50%_50%]"
    },
    {
      src: slideImage3,
      highSrc: slideImage3,
      alt: "WELL Cabin GranLuxe Interior 1",
      class: "tw:object-[50%_50%]"
    },
    {
      src: slideImage4,
      highSrc: slideImage4,
      alt: "WELL Cabin GranLuxe Interior 2",
      class: "tw:object-[20%_80%]"
    },
    {
      src: slideImage5,
      highSrc: slideImage5,
      alt: "WELL Cabin GranLuxe Interior 3",
      class: "tw:object-[20%_80%]"
    },
    {
      src: slideImage6,
      highSrc: slideImage6,
      alt: "WELL Cabin GranLuxe Exterior 3",
      class: "tw:object-[30%_70%]"
    },
  ];
  const cabinLightboxSliderSettings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    initialSlide: activeCabinImageIndex,
    beforeChange: (_, nextSlide) => {
      setActiveCabinImageIndex(nextSlide);
    },
  };

  const isFirstCabinImage = activeCabinImageIndex === 0;
  const isLastCabinImage = activeCabinImageIndex === cabinGalleryImages.length - 1;

  const handleOpenCabinLightbox = (index) => {
    setActiveCabinImageIndex(index);
    onOpenCabinLightbox();
  };

  const handleNextCabinImage = () => {
    lightboxSliderRef.current?.slickNext();
  };

  const handlePrevCabinImage = () => {
    lightboxSliderRef.current?.slickPrev();
  };

  const readCabinGalleryScrollState = () => {
    const gallery = cabinGalleryRef.current;
    if (!gallery) return;

    setCanScrollGalleryPrev(gallery.scrollLeft > 1);
    setCanScrollGalleryNext(gallery.scrollLeft + gallery.clientWidth < gallery.scrollWidth - 1);
  };

  // A single swipe fires dozens of scroll events, and each state update
  // re-renders this whole page. Collapse them into one update per animation
  // frame so dragging the gallery stays smooth.
  const updateCabinGalleryScrollState = () => {
    if (pendingGalleryScrollFrame.current !== null) return;

    pendingGalleryScrollFrame.current = window.requestAnimationFrame(() => {
      pendingGalleryScrollFrame.current = null;
      readCabinGalleryScrollState();
    });
  };

  const scrollCabinGallery = (direction) => {
    const gallery = cabinGalleryRef.current;
    if (!gallery) return;

    const itemWidth = gallery.firstElementChild?.offsetWidth || gallery.clientWidth;
    gallery.scrollBy({ left: direction * itemWidth, behavior: "smooth" });
  };

  useEffect(() => {
    readCabinGalleryScrollState();
    window.addEventListener("resize", updateCabinGalleryScrollState);

    return () => {
      window.removeEventListener("resize", updateCabinGalleryScrollState);

      if (pendingGalleryScrollFrame.current !== null) {
        window.cancelAnimationFrame(pendingGalleryScrollFrame.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isCabinLightboxOpen) {
      return undefined;
    }

    const handleKeydown = (event) => {
      if (event.key === "Escape") {
        onCloseCabinLightbox();
      }

      if (event.key === "ArrowRight") {
        handleNextCabinImage();
      }

      if (event.key === "ArrowLeft") {
        handlePrevCabinImage();
      }
    };

    document.body.classList.add("tw:overflow-hidden");
    window.addEventListener("keydown", handleKeydown);

    return () => {
      document.body.classList.remove("tw:overflow-hidden");
      window.removeEventListener("keydown", handleKeydown);
    };
  }, [isCabinLightboxOpen, onCloseCabinLightbox]);

  return (
    <div className="tw:relative tw:max-w-[1920px] tw:mx-auto tw:font-hanken-grotesk tw:mt-[88px]">
      <div className="tw:max-w-[1920px] tw:m-auto tw:relative">
        <div className="tw:bg-[linear-gradient(87.06deg,#000000_6.43%,rgba(40,42,45,0.3)_46.3%)] tw:w-full tw:h-full tw:absolute tw:top-0 tw:left-0 tw:z-20"></div>
        <LazyImage
          src={WELLCabinGranluxeBanner}
          alt="WELL Cabin GranLuxe Banner"
          wrapperClassName="tw:w-full tw:h-[430px] tw:relative tw:z-0"
          className="tw:w-full tw:h-full tw:object-cover tw:object-[58%_42%]"
        />
        <div className="tw:absolute tw:left-5 tw:bottom-8 tw:lg:left-[82px] tw:lg:bottom-[65px] tw:space-y-[14px] tw:z-50">
          <div className="tw:text-[rgba(219,190,120,1)] tw:text-[8px] tw:tracking-[3px] tw:font-semibold tw:font-hanken-grotesk tw:leading-none tw:uppercase">
            {t("Fleet.Heading")}
          </div>
          <div className="tw:text-[34px] tw:!font-[400] tw:lg:text-[45px] tw:font-libre-caslon-display tw:leading-[58px] tw:text-white tw:font-normal">
            {t("FleetTypePage.WELLCabinGranLuxe.Title")}{" "}
            <span className="tw:text-[26px] tw:!font-[400]">
              {t("FleetTypePage.WELLCabinGranLuxe.BaseVehicle")}
            </span>
          </div>
          <div className={cn("tw:flex tw:items-center tw:gap-x-3 tw:lg:gap-x-4", isJapanese ? "tw:flex-row-reverse tw:justify-end" : "tw:flex-row tw:justify-start")}>
            <span className="tw:font-semibold tw:text-[8px] tw:lg:text-[10px] tw:text-[#D9D9D9] tw:uppercase tw:tracking-[3px]">
              {t("FleetTypePage.WELLCabinGranLuxe.CollaborationWith")}
            </span>
            <LazyImage
              src={PanasonicAutomotiveImageBackgroundBlack}
              alt="Panasonic Automotive"
              wrapperClassName="tw:inline-flex tw:items-center tw:justify-center tw:h-[18px] tw:lg:h-[22px]"
              className="tw:h-full tw:w-auto tw:object-contain"
              skeletonClassName="tw:bg-[#2b2b2b]"
            />
          </div>
        </div>
      </div>

      <div className="tw:sticky tw:top-[88px] tw:z-40" >
        <div className="tw:bg-[#F9F9F9] tw:relative tw:border-b tw:border-b-[#8D8D8D] tw:h-[72px] tw:lg:h-[100px]">
          <div className="tw:flex tw:lg:px-[82px] tw:lg:justify-start tw:px-3 tw:pt-3 tw:lg:pt-[27px] tw:overflow-x-auto tw:whitespace-nowrap tw:absolute tw:w-full tw:[scrollbar-width:none] tw:[&::-webkit-scrollbar]:hidden">
            {tabList.map((tab) => (
              <div
                className={cn(
                  "tw:flex-1 tw:lg:flex-0 tw:text-center tw:flex-shrink-0 tw:h-[60px] tw:lg:h-[73px] tw:px-5 tw:lg:px-[26px] tw:flex tw:items-center tw:justify-center tw:font-hanken-grotesk tw:lg:text-base tw:font-normal tw:lg:font-medium tw:space-x-2 tw:lg:space-x-3",
                  getTypographyClass(14),
                  "WELLCabinGranLuxe" === tab.id
                    ? "tw:bg-white tw:border-x tw:border-x-[#8D8D8D] tw:border-t tw:border-t-[#8D8D8D] tw:text-[#282828] tw:rounded-tl-md tw:rounded-tr-md"
                    : "tw:text-[#8D8D8D] tw:cursor-pointer"
                )}
                onClick={() => push(`?type=${tab.id}`)}
                key={tab.id}
              >
                <div>{t(`FleetTypePage.${tab.id}.Title`)}</div>
              </div>
            ))}
          </div>
        </div>
      </div >

      <div className="tw:lg:px-[82px]" >
        <div className="tw:px-5 tw:lg:px-0 tw:mt-7 tw:lg:mt-[54px]" >
          <div className="tw:flex tw:flex-col tw:gap-y-8 tw:2xl:flex-row tw:items-center tw:rounded-[10px] tw:overflow-hidden tw:bg-[#F9F9F9] tw:2xl:h-[360px]">
            <LazyImage
              src={WELLCabinGranluxeImage}
              alt="WELL Cabin GranLuxe"
              wrapperClassName="tw:w-full tw:lg:min-w-[511px] tw:lg:h-full tw:rounded-t-[10px] tw:lg:rounded-none tw:flex-1"
              className="tw:w-full tw:h-full tw:object-cover"
            />
            <div>
              <div className="tw:px-[25px] tw:lg:px-[48px] tw:lg:pt-[56px] tw:pb-7 tw:lg:pb-[56px] tw:space-y-[10px]">
                <div className="tw:pb-4 tw:border-b tw:border-b-[#F1F1F1] tw:mb-4">
                  <div className="tw:font-semibold tw:text-[#282828] tw:tracking-[3px] tw:text-[8px] tw:font-hanken-grotesk tw:uppercase">
                    {t("FleetTypePage.WELLCabinGranLuxe.Tag")}
                  </div>
                  <div className={cn("tw:text-[#282828] tw:font-libre-caslon-display tw:text-[26px] tw:leading-[35px] tw:lg:leading-[58px]", isJapanese ? "tw:lg:text-[34px]" : "tw:lg:text-[45px]")}>
                    {t("FleetTypePage.WELLCabinGranLuxe.Title")}
                  </div>
                  <div className={cn("tw:flex tw:items-center tw:gap-x-3 tw:lg:gap-x-4 tw:mt-[14px]", isJapanese ? "tw:flex-row-reverse tw:justify-end" : "tw:flex-row tw:justify-start")}>
                    <span className="tw:font-semibold tw:text-[8px] tw:lg:text-[10px] tw:text-[#212529] tw:uppercase tw:tracking-[3px]">
                      {t("FleetTypePage.WELLCabinGranLuxe.CollaborationWith")}
                    </span>
                    <LazyImage
                      src={PanasonicAutomotiveImageBackgroundWhite}
                      alt="Panasonic Automotive"
                      wrapperClassName="tw:inline-flex tw:items-center tw:justify-center tw:h-[18px] tw:lg:h-[22px]"
                      className="tw:h-full tw:w-auto tw:object-contain"
                      skeletonClassName="tw:bg-[#d6d6d6]"
                    />
                  </div>
                </div>
                <div
                  className={cn(
                    "tw:text-[#4E4E4E] tw:font-hanken-grotesk",
                    getTypographyClass(12),
                    getTypographyClass(13, "lg")
                  )}
                >
                  <Trans
                    i18nKey="FleetTypePage.WELLCabinGranLuxe.Description"
                    components={{
                      strong: <strong></strong>,
                    }}
                  />
                </div>
                <Link to={`/${i18n.language}/book`}>
                  <div className="tw:bg-[#D4B055] tw:h-[50px] tw:max-w-[450px] tw:w-auto tw:lg:w-[450px] tw:flex tw:items-center tw:justify-center tw:gap-x-[10px] tw:mt-[24px] tw:rounded-[5px]">
                    <span className="tw:text-white tw:text-[15px] tw:font-bold tw:leading-[22px]">
                      {t("FleetTypePage.WELLCabinGranLuxe.BookNow")}
                    </span>
                    <FiArrowRight size={20} color="#ffffff" />
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div >

        <div className="tw:px-5 tw:lg:px-0 tw:mt-8 tw:lg:mt-[54px] tw:font-hanken-grotesk tw:pb-8 tw:lg:pb-[54px] tw:border-b tw:border-b-[#F1F1F1] tw:text-[#282828] tw:flex tw:flex-col tw:lg:flex-row" >
          <div className="tw:font-medium tw:lg:font-semibold tw:lg:text-[18px] tw:lg:leading-[25px] tw:lg:min-w-[352px]">
            {t("FeaturesAmenities")}
          </div>
          <div className={cn(
            "tw:mt-5 tw:lg:mt-0 tw:grid tw:grid-cols-12 tw:border tw:border-[#F1F1F1] tw:rounded-md tw:w-full",
            getTypographyClass(13),
            getTypographyClass(14, "lg")
          )}>
            <div className={cn("tw:flex tw:flex-col tw:gap-y-3 tw:justify-center tw:items-center tw:flex-1 tw:px-3 tw:md:px-6 tw:border-r tw:border-[#F1F1F1] tw:col-span-4 tw:xl:col-span-2", isJapanese ? "tw:h-[136px]" : "tw:h-[154px]")}>
              <AmenityIcon>
                <PassengerIcon
                  size={AMENITY_ICON_SIZE.passenger}
                  color={AMENITY_ICON_COLOR}
                />
              </AmenityIcon>
              <div
                className={cn(
                  "tw:text-center tw:font-hanken-grotesk tw:text-[#282828]",
                  getTypographyClass(14)
                )}
              >
                <Trans
                  i18nKey="FleetTypePage.WELLCabinGranLuxe.Passengers"
                  values={{ count: 4 }}
                  components={{
                    strong: <strong></strong>,
                    b: <b></b>,
                  }}
                />
              </div>
            </div>

            <div className={cn("tw:flex tw:flex-col tw:gap-y-3 tw:justify-center tw:items-center tw:flex-1 tw:px-3 tw:md:px-6 tw:border-r tw:border-[#F1F1F1] tw:col-span-4 tw:xl:col-span-2", isJapanese ? "tw:h-[136px]" : "tw:h-[154px]")}>
              <AmenityIcon>
                <SuitcaseIcon
                  size={AMENITY_ICON_SIZE.suitcase}
                  color={AMENITY_ICON_COLOR}
                />
              </AmenityIcon>
              <div
                className={cn(
                  "tw:text-center tw:font-hanken-grotesk tw:text-[#282828]",
                  getTypographyClass(14)
                )}
              >
                <Trans
                  i18nKey="FleetTypePage.WELLCabinGranLuxe.Suitcases"
                  values={{ count: "6" }}
                  components={{
                    strong: <strong></strong>,
                    b: <b></b>,
                  }}
                />
                <div className="tw:text-[#8D8D8D] tw:text-[12px] tw:font-[400] tw:leading-4">
                  {t("FleetTypePage.suitcaseNote")}
                </div>
              </div>
            </div>

            <div className={cn("tw:flex tw:flex-col tw:gap-y-3 tw:justify-center tw:items-center tw:flex-1 tw:px-3 tw:md:px-6 tw:border-r tw:border-[#F1F1F1] tw:col-span-4 tw:xl:col-span-2 tw:border-t tw:border-t-[#F1F1F1] tw:xl:border-t-0", isJapanese ? "tw:h-[136px]" : "tw:h-[154px]")}>
              <AmenityIcon>
                <TvIcon
                  size={AMENITY_ICON_SIZE.tv}
                  color={AMENITY_ICON_COLOR}
                />
              </AmenityIcon>
              <div
                className={cn(
                  "tw:text-center tw:font-hanken-grotesk tw:text-[#282828]",
                  getTypographyClass(14)
                )}
              >
                {t("FleetTypePage.WELLCabinGranLuxe.ImmersiveLargeTransparentDisplay")}
              </div>
            </div>

            <div className={cn("tw:flex tw:flex-col tw:gap-y-3 tw:justify-center tw:items-center tw:flex-1 tw:px-3 tw:border-r tw:border-[#F1F1F1] tw:col-span-4 tw:xl:col-span-2 tw:border-t tw:border-t-[#F1F1F1] tw:xl:border-t-0", isJapanese ? "tw:h-[136px]" : "tw:h-[154px]")}>
              <AmenityIcon>
                <VolumeUpIcon
                  size={AMENITY_ICON_SIZE.volumeUp}
                  color={AMENITY_ICON_COLOR}
                />
              </AmenityIcon>
              <div
                className={cn(
                  "tw:text-center tw:font-hanken-grotesk tw:text-[#282828]",
                  getTypographyClass(14)
                )}
              >
                {t("FleetTypePage.WELLCabinGranLuxe.3DSoundSystem")}
              </div>
            </div>

            <div className={cn("tw:flex tw:flex-col tw:gap-y-3 tw:justify-center tw:items-center tw:flex-1 tw:px-3 tw:md:px-6 tw:border-r tw:border-[#F1F1F1] tw:col-span-4 tw:xl:col-span-2 tw:border-t tw:border-t-[#F1F1F1] tw:xl:border-t-0", isJapanese ? "tw:h-[136px]" : "tw:h-[154px]")}>
              <AmenityIcon>
                <AromaIcon
                  size={AMENITY_ICON_SIZE.aroma}
                  color={AMENITY_ICON_COLOR}
                />
              </AmenityIcon>
              <div
                className={cn(
                  "tw:text-center tw:font-hanken-grotesk tw:text-[#282828]",
                  getTypographyClass(14)
                )}
              >
                {t("Aroma")}
              </div>
            </div>

            <div className={cn("tw:flex tw:flex-col tw:gap-y-3 tw:justify-center tw:items-center tw:flex-1 tw:px-6 tw:col-span-4 tw:xl:col-span-2 tw:border-t tw:border-t-[#F1F1F1] tw:xl:border-t-0", isJapanese ? "tw:h-[136px]" : "tw:h-[154px]")}>
              <AmenityIcon>
                <WifiIcon
                  size={AMENITY_ICON_SIZE.wifi}
                  color={AMENITY_ICON_COLOR}
                />
              </AmenityIcon>
              <div
                className={cn(
                  "tw:text-center tw:font-hanken-grotesk tw:text-[#282828]",
                  getTypographyClass(14)
                )}
              >
                {t("InCarWiFi")}
              </div>
            </div>
          </div>
        </div >
        <div className="tw:px-5 tw:lg:px-0 tw:border-b tw:border-[#F1F1F1] tw:pb-[54px]">
          <div className="tw:flex tw:flex-col tw:md:flex-row tw:gap-[10px]">
            <div className="tw:relative tw:flex-1 tw:min-w-0 tw:h-[240px] tw:md:h-auto tw:md:aspect-[16/14] tw:overflow-hidden tw:rounded-[5px]">
              <img src={DisplayImage} alt="Display" draggable={false} className="tw:h-full tw:w-full tw:object-cover tw:rounded-[5px]" />
              <div className="tw:absolute tw:inset-0 tw:bg-gradient-to-t tw:from-[rgba(0,0,0,0.45)] tw:via-[rgba(0,0,0,0.2)] tw:to-[rgba(0,0,0,0)]"></div>
              <div className="tw:absolute tw:left-5 tw:right-5 tw:bottom-5 tw:text-white tw:font-hanken-grotesk">
                <div className="tw:font-semibold tw:text-[18px] tw:leading-[25px]">{t("FleetTypePage.WELLCabinGranLuxe.FeatureCards.DisplayTitle")}</div>
                <div className="tw:mt-2 tw:text-[14px] tw:leading-5 tw:text-[#DADADA]">
                  <Trans
                    i18nKey="FleetTypePage.WELLCabinGranLuxe.FeatureCards.DisplayDescription"
                    components={{
                      strong: <strong className="tw:font-bold"></strong>,
                      br: <br />,
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="tw:relative tw:flex-1 tw:min-w-0 tw:h-[240px] tw:md:h-auto tw:md:aspect-[16/14] tw:overflow-hidden tw:rounded-[5px]">
              <img src={AudioImage} alt="Audio" draggable={false} className="tw:h-full tw:w-full tw:object-cover tw:rounded-[5px]" />
              <div className="tw:absolute tw:inset-0 tw:bg-gradient-to-t tw:from-[rgba(0,0,0,0.45)] tw:via-[rgba(0,0,0,0.2)] tw:to-[rgba(0,0,0,0)]"></div>
              <div className="tw:absolute tw:left-5 tw:right-5 tw:bottom-5 tw:text-white tw:font-hanken-grotesk">
                <div className="tw:font-semibold tw:text-[18px] tw:leading-[25px]">{t("FleetTypePage.WELLCabinGranLuxe.FeatureCards.AudioTitle")}</div>
                <div className="tw:mt-2 tw:text-[14px] tw:leading-5 tw:text-[#DADADA]">
                  <Trans
                    i18nKey="FleetTypePage.WELLCabinGranLuxe.FeatureCards.AudioDescription"
                    components={{
                      strong: <strong className="tw:font-bold"></strong>,
                      br: <br />,
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="tw:relative tw:flex-1 tw:min-w-0 tw:h-[240px] tw:md:h-auto tw:md:aspect-[16/14] tw:overflow-hidden tw:rounded-[5px]">
              <img src={LightingImage} alt="Lighting" draggable={false} className="tw:h-full tw:w-full tw:object-cover tw:rounded-[5px]" />
              <div className="tw:absolute tw:inset-0 tw:bg-gradient-to-t tw:from-[rgba(0,0,0,0.45)] tw:via-[rgba(0,0,0,0.2)] tw:to-[rgba(0,0,0,0)]"></div>
              <div className="tw:absolute tw:left-5 tw:right-5 tw:bottom-5 tw:text-white tw:font-hanken-grotesk">
                <div className="tw:font-semibold tw:text-[18px] tw:leading-[25px]">{t("FleetTypePage.WELLCabinGranLuxe.FeatureCards.LightingTitle")}</div>
                <div className="tw:mt-2 tw:text-[14px] tw:leading-5 tw:text-[#DADADA]">
                  <Trans
                    i18nKey="FleetTypePage.WELLCabinGranLuxe.FeatureCards.LightingDescription"
                    components={{
                      strong: <strong className="tw:font-bold"></strong>,
                      br: <br />,
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="tw:relative tw:flex-1 tw:min-w-0 tw:h-[240px] tw:md:h-auto tw:md:aspect-[16/14] tw:overflow-hidden tw:rounded-[5px]">
              <img src={AromaImage} alt="Audio" draggable={false} className="tw:h-full tw:w-full tw:object-cover tw:rounded-[5px]" />
              <div className="tw:absolute tw:inset-0 tw:bg-gradient-to-t tw:from-[rgba(0,0,0,0.45)] tw:via-[rgba(0,0,0,0.2)] tw:to-[rgba(0,0,0,0)]"></div>
              <div className="tw:absolute tw:left-5 tw:right-5 tw:bottom-5 tw:text-white tw:font-hanken-grotesk">
                <div className="tw:font-semibold tw:text-[18px] tw:leading-[25px]">{t("FleetTypePage.WELLCabinGranLuxe.FeatureCards.AromaTitle")}</div>
                <div className="tw:mt-2 tw:text-[14px] tw:leading-5 tw:text-[#DADADA]">
                  <Trans
                    i18nKey="FleetTypePage.WELLCabinGranLuxe.FeatureCards.AromaDescription"
                    components={{
                      strong: <strong className="tw:font-bold"></strong>,
                      br: <br />,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className={cn(`tw:mt-7 tw:text-center tw:text-[#282828] tw:text-[14px] tw:leading-5 tw:font-hanken-grotesk`, getTypographyClass(14))}>
            <Trans
              i18nKey="FleetTypePage.WELLCabinGranLuxe.FeatureCards.Caption"
              components={{
                br: <br />,
                strong: <strong className="tw:font-bold"></strong>,
                i: <i className="tw:font-italic"></i>,
              }}
            />
          </div>
        </div>

        <div className="tw:px-5 tw:lg:px-0 tw:mt-8 tw:lg:mt-[54px] tw:font-hanken-grotesk tw:pb-8 tw:lg:pb-[54px] tw:text-[#282828] tw:flex tw:flex-col tw:lg:flex-row" >
          <div className="tw:font-medium tw:lg:font-semibold tw:lg:text-[18px] tw:lg:leading-[25px] tw:lg:min-w-[352px]">
            {t("Vehicle.Details")}
          </div>
          <div
            className={cn(
              "tw:mt-5 tw:lg:mt-0",
              getTypographyClass(13),
              getTypographyClass(14, "lg")
            )}
          >
            {isJapanese ? (
              <>
                <Trans
                  i18nKey="FleetTypePage.WELLCabinGranLuxe.VehicleDetails.Description1"
                  components={{
                    strong: <strong></strong>,
                  }}
                />
                <div className="tw:my-2">
                  <Trans
                    i18nKey="FleetTypePage.WELLCabinGranLuxe.VehicleDetails.Description2"
                    components={{
                      strong: <strong></strong>,
                    }}
                  />
                </div>
                <Trans
                  i18nKey="FleetTypePage.WELLCabinGranLuxe.VehicleDetails.Description3"
                  components={{
                    strong: <strong></strong>,
                  }}
                />
              </>
            ) : (
              <>
                <Trans
                  i18nKey="FleetTypePage.WELLCabinGranLuxe.VehicleDetails.Description1"
                  components={{
                    strong: <strong></strong>,
                  }}
                />
                <div className="tw:mt-2">
                  <Trans
                    i18nKey="FleetTypePage.WELLCabinGranLuxe.VehicleDetails.Description2"
                    components={{
                      strong: <strong></strong>,
                    }}
                  />
                </div>
              </>
            )}
          </div>
        </div >

        <div className="tw:px-5 tw:lg:px-0 tw:mt-8 tw:lg:mt-[54px] tw:font-hanken-grotesk tw:pb-8 tw:lg:pb-[54px] tw:border-b tw:border-b-[#F1F1F1] tw:text-[#282828]">
          <div className="tw:mt-4 tw:pb-[52px] tw:relative">
            <div
              ref={cabinGalleryRef}
              onScroll={updateCabinGalleryScrollState}
              className="tw:flex tw:overflow-x-auto tw:snap-x tw:snap-mandatory tw:overscroll-x-contain tw:[scrollbar-width:none] tw:[&::-webkit-scrollbar]:hidden"
            >
              {cabinGalleryImages.map((image, index) => (
                <div
                  className="tw:shrink-0 tw:w-1/2 tw:md:w-1/3 tw:snap-start tw:px-[3px] tw:md:px-2"
                  key={image.alt}
                >
                  <button
                    type="button"
                    onClick={() => handleOpenCabinLightbox(index)}
                    className="tw:w-full tw:text-left"
                    aria-label={`Open ${image.alt} in lightbox`}
                  >
                    <LazyImage
                      src={image.src}
                      alt={image.alt}
                      wrapperClassName="tw:rounded-md tw:overflow-hidden tw:h-[88px] tw:md:h-[200px] tw:lg:h-[324px]"
                      className={`tw:w-full tw:h-full tw:object-cover tw:transition-transform tw:hover:scale-[1.02] ${image.class}`}
                    />
                  </button>
                </div>
              ))}
            </div>
            <div className="tw:absolute tw:right-0 tw:bottom-0 tw:flex tw:gap-x-3">
              <CabinGalleryArrow
                direction="prev"
                disabled={!canScrollGalleryPrev}
                onClick={() => scrollCabinGallery(-1)}
              />
              <CabinGalleryArrow
                direction="next"
                disabled={!canScrollGalleryNext}
                onClick={() => scrollCabinGallery(1)}
              />
            </div>
          </div>
        </div>

        <div className="tw:px-5 tw:lg:px-0 tw:mt-8 tw:lg:mt-[54px] tw:font-hanken-grotesk tw:pb-8 tw:lg:pb-[54px] tw:text-[#282828] tw:flex tw:flex-col tw:lg:flex-row tw:border-b tw:border-b-[#F1F1F1]">
          <div className="tw:font-medium tw:lg:font-semibold tw:lg:text-[18px] tw:lg:leading-[25px] tw:lg:min-w-[352px] tw:flex tw:lg:flex-col tw:gap-x-6">
            <div>
              <View360Icon aria-label="360-degree" role="img" />
            </div>
            <div className="tw:font-semibold tw:leading-[25px] tw:text-lg tw:mt-4">
              {t("360-degree-view")}
            </div>
          </div>
          <div
            className={cn(
              "tw:mt-5 tw:lg:mt-0 tw:w-full",
              getTypographyClass(13),
              getTypographyClass(14, "lg")
            )}
          >
            {t("FleetTypePage.WELLCabinGranLuxe.360View.Description")}
            <div className="tw:!mt-6 tw:!w-full">
              <Video360
                src={"https://www.youtube.com/embed/3hbqbGYCaiI?si=3-GYvktvAUFO4bFv"}
                title="WELL Cabin GranLuxe VR 360 Video by THE LUXURY FLEET Co., Ltd."
              />
            </div>
          </div>
        </div>

        <div className="tw:px-5 tw:lg:px-0 tw:mt-8 tw:lg:mt-[54px] tw:font-hanken-grotesk tw:pb-8 tw:lg:pb-[54px] tw:text-[#282828] tw:flex tw:flex-col tw:lg:flex-row" >
          <div className="tw:font-medium tw:lg:font-semibold tw:lg:text-[18px] tw:lg:leading-[25px] tw:lg:min-w-[250px]">
            <div className="tw:font-semibold tw:leading-[25px] tw:text-lg tw:mt-4">
              {t("Specifications")}
            </div>
          </div>
          <div
            className={cn(
              "tw:mt-5 tw:lg:mt-0 tw:w-full tw:flex tw:flex-col tw:xl:flex-row tw:pb-8 tw:gap-y-8",
              getTypographyClass(13),
              getTypographyClass(14, "lg")
            )}
          >
            <div className="tw:mx-2">
              <img
                src={WELLCabinGranluxeSpecificationImage}
                className="tw:w-[470px] tw:max-w-full tw:h-auto"
                alt="WELL Cabin GranLuxe Specification"
              />
            </div>
            {/* Capped to the Reserve Now button width so the spec rows don't stretch across the page */}
            <div className="tw:flex-1">
              <div className="tw:text-[#282828] tw:text-[8px] tw:tracking-[3px] tw:font-semibold tw:uppercase">
                {t("FleetTypePage.WELLCabinGranLuxe.Tag")}
              </div>
              <div className="tw:text-[#282828] tw:font-libre-caslon-display tw:text-[34px] tw:leading-[45px] tw:mt-3">
                {t("FleetTypePage.WELLCabinGranLuxe.Title")}
              </div>
              <div className="tw:text-[#5D5C5C] tw:font-libre-caslon-display tw:text-[22px] tw:leading-[30px] tw:mt-1">
                {t("FleetTypePage.WELLCabinGranLuxe.BaseVehicle")}
              </div>
              <div className="tw:py-8 tw:border-t tw:border-t-[#F1F1F1] tw:flex tw:items-start tw:mt-8">
                <div className="tw:w-[195px] tw:text-[#8D8D8D] tw:font-medium">
                  {t("Capacity")}
                </div>
                <div className="tw:space-y-[10px]">
                  <div className="tw:flex tw:items-center tw:gap-x-[6px]">
                    <div>
                      <PassengerIcon />
                    </div>
                    <span
                      className={cn(
                        "tw:text-[#282828]",
                        getTypographyClass(14)
                      )}
                    >
                      {t("Specification.UpToPassengers", { count: 4 })}
                    </span>
                  </div>
                  <div className="tw:flex tw:items-center tw:gap-x-[6px]">
                    <div>
                      <SuitcaseIcon />
                    </div>
                    <span
                      className={cn(
                        "tw:text-[#282828]",
                        getTypographyClass(14)
                      )}
                    >
                      {t("Specification.UpToSuitcases", { count: "6" })}
                    </span>
                  </div>
                </div>
              </div>
              <div className="tw:py-8 tw:border-t tw:border-t-[#F1F1F1] tw:flex tw:items-start">
                <div className="tw:w-[195px] tw:text-[#8D8D8D] tw:font-medium tw:capitalize">
                  {t("Interior.Space")}
                </div>
                <div className="tw:space-y-[10px]">
                  <div className="tw:flex tw:items-center tw:gap-x-[6px]">
                    <div
                      className={cn(
                        "tw:text-[#282828] tw:flex tw:items-center tw:gap-x-1",
                        getTypographyClass(14)
                      )}
                    >
                      <span className="tw:font-sans tw:text-base">&#9312;</span>{" "}
                      <span>{t("Drivers.Seat")}</span>
                    </div>
                  </div>
                  <div className="tw:flex tw:items-center tw:gap-x-[6px]">
                    <div
                      className={cn(
                        "tw:text-[#282828] tw:flex tw:items-center tw:gap-x-1",
                        getTypographyClass(14)
                      )}
                    >
                      <span className="tw:font-sans tw:text-base">&#9313;</span>{" "}
                      <span>{t("Passenger.Area")}</span>
                    </div>
                  </div>
                  <div className="tw:flex tw:items-center tw:gap-x-[6px]">
                    <div
                      className={cn(
                        "tw:text-[#282828] tw:flex tw:items-center tw:gap-x-1",
                        getTypographyClass(14)
                      )}
                    >
                      <span className="tw:font-sans tw:text-base">&#9314;</span>{" "}
                      <span>{t("Luggage.Area")}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="tw:py-8 tw:border-t tw:border-t-[#F1F1F1] tw:flex tw:items-start">
                <div className="tw:w-[195px] tw:text-[#8D8D8D] tw:font-medium">
                  {t("Size.Specifications")}
                </div>
                <div className="tw:space-y-[10px]">
                  <div className="tw:flex tw:items-center tw:gap-x-[6px]">
                    <div
                      className={cn(
                        "tw:text-[#282828] tw:flex tw:items-center tw:gap-x-1",
                        getTypographyClass(14)
                      )}
                    >
                      {t("Length")}: 5,380mm
                    </div>
                  </div>
                  <div className="tw:flex tw:items-center tw:gap-x-[6px]">
                    <div
                      className={cn(
                        "tw:text-[#282828] tw:flex tw:items-center tw:gap-x-1",
                        getTypographyClass(14)
                      )}
                    >
                      {t("Width")}: 1,880mm
                    </div>
                  </div>
                  <div className="tw:flex tw:items-center tw:gap-x-[6px]">
                    <div
                      className={cn(
                        "tw:text-[#282828] tw:flex tw:items-center tw:gap-x-1",
                        getTypographyClass(14)
                      )}
                    >
                      {t("Height")}: 2,280mm
                    </div>
                  </div>
                </div>
              </div>
              <Link to={`/${i18n.language}/book`}>
                <div className="tw:bg-[#D4B055] tw:h-[50px] tw:max-w-[450px] tw:w-full tw:lg:w-[450px] tw:flex tw:items-center tw:justify-center tw:gap-x-[10px] tw:mt-[14px] tw:rounded-[5px]">
                  <span className="tw:text-white tw:text-[15px] tw:font-bold tw:leading-[22px]">
                    {t("FleetTypePage.WELLCabinGranLuxe.ReserveNow")}
                  </span>
                  <FiArrowRight size={20} color="#ffffff" />
                </div>
              </Link>
            </div>
          </div>
        </div >
      </div >
      {/* Escape, arrow keys and page-scroll lock are handled by the effect above. */}
      <MotionLightbox
        show={isCabinLightboxOpen}
        onClose={onCloseCabinLightbox}
        label="Cabin gallery lightbox"
        closeOnEscape={false}
        lockScroll={false}
        className="tw:px-4"
      >
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onCloseCabinLightbox();
            }}
            className="tw:absolute tw:top-4 tw:right-4 tw:z-[130] tw:text-white tw:hover:text-[#D4B055] tw:transition-colors"
            aria-label="Close lightbox"
          >
            <FiX size={40} />
          </button>

          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              handlePrevCabinImage();
            }}
            disabled={isFirstCabinImage}
            className={cn(
              "tw:absolute tw:left-3 tw:lg:left-6 tw:top-1/2 tw:-translate-y-1/2 tw:z-[130]",
              (cabinGalleryImages.length <= 1 || isFirstCabinImage) && "tw:hidden"
            )}
            aria-label="Previous image"
          >
            <span className="tw:rounded-full! tw:shrink-0 tw:flex tw:items-center tw:justify-center tw:border tw:border-[#282828] tw:transition-colors tw:w-[36px] tw:h-[36px] tw:lg:w-[44px] tw:lg:h-[44px] tw:bg-black tw:hover:bg-[#282828]">
              <FiArrowLeft className="tw:text-[18px] tw:lg:text-[22px]" color="white" />
            </span>
          </button>

          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              handleNextCabinImage();
            }}
            disabled={isLastCabinImage}
            className={cn(
              "tw:absolute tw:right-3 tw:lg:right-6 tw:top-1/2 tw:-translate-y-1/2 tw:z-[130]",
              (cabinGalleryImages.length <= 1 || isLastCabinImage) && "tw:hidden"
            )}
            aria-label="Next image"
          >
            <span className="tw:rounded-full! tw:shrink-0 tw:flex tw:items-center tw:justify-center tw:border tw:border-[#282828] tw:transition-colors tw:w-[36px] tw:h-[36px] tw:lg:w-[44px] tw:lg:h-[44px] tw:bg-black tw:hover:bg-[#282828]">
              <FiArrowRight className="tw:text-[18px] tw:lg:text-[22px]" color="white" />
            </span>
          </button>

          <div
            className="tw:relative tw:w-full tw:max-w-[800px]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="tw:relative tw:overflow-hidden">
              <Slider
                ref={lightboxSliderRef}
                {...cabinLightboxSliderSettings}
                className="tw:!static"
              >
                {cabinGalleryImages.map((image) => (
                  <div key={`lightbox-${image.alt}`} className="tw:!flex tw:!items-center tw:!justify-center">
                    <LazyImage
                      src={image.highSrc}
                      alt={image.alt}
                      wrapperClassName="tw:!w-full"
                      className="tw:w-full tw:h-auto tw:object-contain"
                    />
                  </div>
                ))}
              </Slider>
            </div>
          </div>
      </MotionLightbox>
      < BookSection />
    </div >
  );
};

export default WELLCabinGranluxe;